import classnames from "classnames";
import { useParams } from "react-router-dom";
import { makeStyles } from "components/providers/makeStyles";
import { T } from "../../util/t";
import { isTeamFormat } from "../../../utils/utils";
import { EntriesModalWithTrigger } from "../eventDivision/entriesModal/EntriesModal";
import { SimpleListItemLink, SimpleListItem } from "../lists/SimpleList";
import { Event, EventDivision, EventStatus, PaymentOptions } from "graphql/generated/types";
import { EventDivisionItemAside } from "./EventDivisionItemAside";

const useStyles = makeStyles(theme => ({
    caption: {
        marginTop: theme.spacing(1)
    },
    totalAthletes: {
        "&::before": {
            content: "'•'",
            margin: theme.spacing(0, 0.75)
        }
    },
    availableSpots: {
        color: theme.palette.primary.main,
        "&.hasContentBefore::before": {
            content: "'-'",
            margin: theme.spacing(0, 0.5),
            color: theme.palette.text.primary
        }
    }
}));

const getPrice = (priceIndex: number, paymentOptions: PaymentOptions) => priceIndex > -1 ? paymentOptions?.divisions?.[priceIndex].price : paymentOptions.defaultDivisionPrice;
const getDivisionPrice = (eventDivisionId: EventDivision["id"], paymentOptions: PaymentOptions) => getPrice(((paymentOptions || {}).divisions || []).map(d => d && d.price != null && d.id).indexOf(eventDivisionId), paymentOptions || {});

const PUBLISHED_EVENT_STATUSES: (keyof Pick<Record<EventStatus, string>, "published" | "on" | "on_hold" | "finished" | "results_published">)[] = [
    "published",
    "on",
    "on_hold",
    "finished",
    "results_published"
];

interface Props {
    shouldLink: boolean;
    eventDivision: EventDivision;
    event: Event;
    media?: Event["media"];
}

export const EventDivisionItem: React.FC<Props> = ({ shouldLink, eventDivision, event, media }) => {
    const { id } = useParams<{ id: string }>();
    const { paymentOptions, status } = event;
    const classes = useStyles();
    const baseLink = `/events/${id}/divisions/${eventDivision.eventDivisionId || eventDivision.id}`;
    const linkToDraw = eventDivision.status === "drawn" || event.hideEntries;
    const link = linkToDraw ? baseLink : baseLink + "/entries";
    const divisionPrice = getDivisionPrice(eventDivision.id, paymentOptions || {});
    const entryLimit = eventDivision.entryLimit?.limit;
    const availableSpots = entryLimit && entryLimit - eventDivision.entryCount;

    const Component = shouldLink ? SimpleListItemLink : SimpleListItem;

    return (
        <Component
            to={link}
            title={eventDivision.division.name}
            subtitle={eventDivision.eventDivisions.length > 0 ? eventDivision.eventDivisions.map(ed => ed.division.name).join(", ") : undefined}
            aside={PUBLISHED_EVENT_STATUSES.includes(status as typeof PUBLISHED_EVENT_STATUSES[number]) && media && <EventDivisionItemAside media={media} eventDivision={eventDivision} shouldLink={shouldLink} />}
            description={status !== "scheduled" && status !== "cancelled"
                ? ["drawn", "upcoming"].indexOf(status) !== -1
                    ? <>
                        {divisionPrice != null && <T price={divisionPrice} currency={(paymentOptions || {}).currency}>division_price</T>}

                        {!event.hideEntries &&
                        <span className={classnames({ [classes.totalAthletes]: divisionPrice != null })}>
                            {eventDivision.entryCount === 0 ?
                                <T x={eventDivision.entryCount}>x_entered</T> :
                                <EntriesModalWithTrigger eventDivisionId={eventDivision.id}>
                                    <T x={eventDivision.entryCount}>x_entered</T>
                                </EntriesModalWithTrigger>
                            }
                        </span>}

                        {entryLimit &&
                        <span className={classnames(classes.availableSpots, { hasContentBefore: divisionPrice != null || !event.hideEntries })}>
                            {availableSpots && availableSpots > 0
                                ? <T spots={availableSpots}>spots_left</T>
                                : <T>Division full 😥</T>}
                        </span>}
                    </>
                    : <T count={eventDivision.entryCount}>{isTeamFormat(eventDivision.formatDefinition) ? "team_count" : "athlete_count"}</T>
                : null}
        />
    );
};
